import { layerAABB } from './model/bounds';
import { leafLayers } from './model/tree';
import type { EditorDocument, Layer, Rect, Vec2 } from './model/types';
import { normaliseDegrees } from './move';
import { textBox } from './paint/text';
import { clampRect, normalizeRect, type Size2 } from './selection';

/**
 * Transform planning.
 *
 * A transform is split in two halves, and the split is the point:
 *
 *  - **Here**: pure geometry. Given a document and an operation, work out the
 *    new canvas size and, per layer, where it lands and what must happen to its
 *    pixels. Deterministic, unit-tested, no canvas.
 *  - `src/io/raster.ts`: executes the described pixel work with a canvas.
 *
 * Because the plan is data, a whole multi-layer transform becomes one history
 * entry and undo restores the original exactly.
 */

export type PixelOp =
  | { kind: 'crop'; sourceX: number; sourceY: number; width: number; height: number }
  | { kind: 'rotate'; direction: 1 | -1 }
  | { kind: 'flip'; axis: 'horizontal' | 'vertical' }
  | { kind: 'resample'; width: number; height: number };

export interface LayerPlan {
  id: string;
  /** New top-left in the new document. */
  x: number;
  y: number;
  /** Pixel work to bake into the layer, or null to leave pixels untouched. */
  op: PixelOp | null;
  /**
   * New intrinsic size when the pixel work changes it. Crop and resample bake
   * the layer's scale into their output, so the executor resets scale to 1;
   * rotate and flip do not touch scale at all.
   */
  naturalWidth?: number;
  naturalHeight?: number;
  /** Replacement angle when the operation changes it (rotate 90 and flip). */
  angle?: number;
  /** Replacement scale when the operation changes it (resample on live layers). */
  scaleX?: number;
  scaleY?: number;
  /** Layer falls entirely outside the new canvas. */
  remove?: boolean;
}

export interface TransformPlan {
  /** History label, e.g. "Crop to Selection". */
  label: string;
  width: number;
  height: number;
  layers: LayerPlan[];
}

export type ResizeAnchor = 'top-left' | 'center' | 'bottom-right';

const ANCHOR_FACTORS: Record<ResizeAnchor, { x: number; y: number }> = {
  'top-left': { x: 0, y: 0 },
  center: { x: 0.5, y: 0.5 },
  'bottom-right': { x: 1, y: 1 },
};

/** Rendered bounds of a layer in document pixels. */
export function intersectRect(a: Rect, b: Rect): Rect | null {
  const left = Math.max(a.x, b.x);
  const top = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  if (right <= left || bottom <= top) return null;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function passthrough(layer: Layer): LayerPlan {
  return { id: layer.id, x: layer.x, y: layer.y, op: null };
}

function centreOf(bounds: Rect): Vec2 {
  return { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
}

/** Where a point lands when the canvas rotates 90° about its own centre. */
function rotateAboutCanvas(point: Vec2, doc: Size2, direction: 1 | -1): Vec2 {
  return direction === 1
    ? { x: doc.height - point.y, y: point.x }
    : { x: point.y, y: doc.width - point.x };
}

/** Where a point lands when the canvas mirrors about one of its axes. */
function mirrorAboutCanvas(point: Vec2, doc: Size2, axis: 'horizontal' | 'vertical'): Vec2 {
  return axis === 'horizontal'
    ? { x: doc.width - point.x, y: point.y }
    : { x: point.x, y: doc.height - point.y };
}

/**
 * The box a live (non-raster) layer occupies: text from its text box, shape
 * layers from their own fields, everything else a point. Rotation is about
 * its centre, exactly like the raster layers.
 */
function liveBox(layer: Layer): Rect {
  if (layer.type === 'text') {
    const box = textBox(layer);
    return {
      x: layer.x,
      y: layer.y,
      width: box.width * layer.scaleX,
      height: box.height * layer.scaleY,
    };
  }
  if (layer.type === 'shape') {
    return {
      x: layer.x,
      y: layer.y,
      width: layer.width * layer.scaleX,
      height: layer.height * layer.scaleY,
    };
  }
  return { x: layer.x, y: layer.y, width: 0, height: 0 };
}

/**
 * Crop the document to `rect`.
 *
 * Raster layers (image, paint) whose pixels line up with the crop — angle a
 * multiple of 360° — have the surviving region baked into their raster, mask
 * included. Everything else — live text, rotated rasters, the schema-surface
 * types — translates with the canvas instead: exact and non-destructive,
 * because the pre-image of an axis-aligned crop under an arbitrary rotation
 * is not axis-aligned, and straightening it would resample pixels the user
 * never asked to touch. Pixels left outside the canvas simply do not render.
 */
export function planCrop(doc: EditorDocument, rect: Rect): TransformPlan | null {
  const target = clampRect(normalizeRect(rect), {
    x: 0,
    y: 0,
    width: doc.width,
    height: doc.height,
  });
  if (target.width < 1 || target.height < 1) return null;

  const layers = leafLayers(doc.layers).map((layer): LayerPlan => {
    if (layer.type === 'fill') return passthrough(layer);

    const croppable = (layer.type === 'image' || layer.type === 'paint') && layer.angle % 360 === 0;
    if (!croppable) {
      return { ...passthrough(layer), x: layer.x - target.x, y: layer.y - target.y };
    }

    const bounds = layerAABB(layer, doc);
    if (!bounds) return passthrough(layer);
    const overlap = intersectRect(bounds, target);
    if (!overlap || overlap.width < 1 || overlap.height < 1) {
      return { id: layer.id, x: 0, y: 0, op: null, remove: true };
    }

    const sourceX = (overlap.x - bounds.x) / layer.scaleX;
    const sourceY = (overlap.y - bounds.y) / layer.scaleY;
    const width = overlap.width / layer.scaleX;
    const height = overlap.height / layer.scaleY;

    return {
      id: layer.id,
      x: overlap.x - target.x,
      y: overlap.y - target.y,
      op: { kind: 'crop', sourceX, sourceY, width, height },
      naturalWidth: Math.max(1, Math.round(width)),
      naturalHeight: Math.max(1, Math.round(height)),
    };
  });

  return {
    label: 'Crop to Selection',
    width: Math.round(target.width),
    height: Math.round(target.height),
    layers,
  };
}

/**
 * Rotate the whole document 90° (direction 1 = clockwise, -1 = counter-clockwise).
 *
 * Raster layers have the quarter-turn baked into their pixels (mask included)
 * and keep their scale — the rotation commutes with it, so what the user sees
 * is exactly the canvas turned. Live layers (text, and the schema-surface
 * types) rotate in place: their angle composes and their centre maps to the
 * same place every other point on the canvas maps to.
 */
export function planRotate90(doc: EditorDocument, direction: 1 | -1): TransformPlan {
  const width = doc.height;
  const height = doc.width;

  const layers = leafLayers(doc.layers).map((layer): LayerPlan => {
    if (layer.type === 'fill') return passthrough(layer);

    if (layer.type === 'image' || layer.type === 'paint') {
      const bounds = layerAABB(layer, doc);
      if (!bounds) return passthrough(layer);
      // Position follows the layer's centre — the one point that survives
      // both the canvas rotation and the layer's own. Top-left arithmetic
      // silently misplaced scaled layers, because the swap only matches the
      // drawn box when scale is 1.
      const centre = rotateAboutCanvas(centreOf(bounds), doc, direction);
      return {
        id: layer.id,
        x: Math.round(centre.x - (layer.naturalHeight * layer.scaleX) / 2),
        y: Math.round(centre.y - (layer.naturalWidth * layer.scaleY) / 2),
        op: { kind: 'rotate', direction },
        naturalWidth: layer.naturalHeight,
        naturalHeight: layer.naturalWidth,
      };
    }

    const box = liveBox(layer);
    const centre = rotateAboutCanvas(centreOf(box), doc, direction);
    return {
      id: layer.id,
      x: Math.round(centre.x - box.width / 2),
      y: Math.round(centre.y - box.height / 2),
      op: null,
      angle: normaliseDegrees(layer.angle + direction * 90),
    };
  });

  return {
    label: direction === 1 ? 'Rotate 90° Clockwise' : 'Rotate 90° Counter-Clockwise',
    width,
    height,
    layers,
  };
}

/**
 * Mirror the whole document about its vertical or horizontal axis.
 *
 * Raster layers have the mirror baked into their pixels about the raster's own
 * centre and keep their scale — which is only correct when the layer's angle
 * flips sign with it, so the plan carries the negated angle. Live text layers
 * cannot mirror their glyphs (the model's scale must stay positive), so the
 * executor rasterizes them; the plan carries the same geometry either way.
 */
export function planFlip(doc: EditorDocument, axis: 'horizontal' | 'vertical'): TransformPlan {
  const layers = leafLayers(doc.layers).map((layer): LayerPlan => {
    if (layer.type === 'fill') return passthrough(layer);

    if (layer.type === 'image' || layer.type === 'paint') {
      const bounds = layerAABB(layer, doc);
      if (!bounds) return passthrough(layer);
      // The centre comes from the AABB (the box actually drawn), but the
      // offset comes from the drawn size: the mirrored raster keeps its own
      // dimensions, and the AABB of a rotated layer is larger than they are.
      const centre = mirrorAboutCanvas(centreOf(bounds), doc, axis);
      return {
        id: layer.id,
        x: Math.round(centre.x - (layer.naturalWidth * layer.scaleX) / 2),
        y: Math.round(centre.y - (layer.naturalHeight * layer.scaleY) / 2),
        op: { kind: 'flip', axis },
        angle: normaliseDegrees(-layer.angle),
      };
    }

    const box = liveBox(layer);
    const centre = mirrorAboutCanvas(centreOf(box), doc, axis);
    return {
      id: layer.id,
      x: Math.round(centre.x - box.width / 2),
      y: Math.round(centre.y - box.height / 2),
      op: { kind: 'flip', axis },
      angle: normaliseDegrees(-layer.angle),
    };
  });

  return {
    label: axis === 'horizontal' ? 'Flip Horizontal' : 'Flip Vertical',
    width: doc.width,
    height: doc.height,
    layers,
  };
}

/**
 * Resample the image content to a new size (the canvas follows).
 *
 * Raster layers have their scale baked into the pixels so the model stays
 * simple afterwards: scale 1 and an intrinsic size that matches what is on
 * screen. Live layers (text) scale the same way the move tool scales them —
 * through their scale fields, never by rewriting the text — and every layer's
 * position follows the canvas scale about the origin.
 */
export function planImageResize(doc: EditorDocument, target: Size2): TransformPlan | null {
  if (target.width < 1 || target.height < 1) return null;
  const factorX = target.width / doc.width;
  const factorY = target.height / doc.height;

  const layers = leafLayers(doc.layers).map((layer): LayerPlan => {
    if (layer.type === 'fill') return passthrough(layer);

    if (layer.type === 'image' || layer.type === 'paint') {
      const naturalWidth = Math.max(1, Math.round(layer.naturalWidth * layer.scaleX * factorX));
      const naturalHeight = Math.max(1, Math.round(layer.naturalHeight * layer.scaleY * factorY));
      return {
        id: layer.id,
        x: Math.round(layer.x * factorX),
        y: Math.round(layer.y * factorY),
        op: { kind: 'resample', width: naturalWidth, height: naturalHeight },
        naturalWidth,
        naturalHeight,
      };
    }

    return {
      id: layer.id,
      x: Math.round(layer.x * factorX),
      y: Math.round(layer.y * factorY),
      op: null,
      scaleX: layer.scaleX * factorX,
      scaleY: layer.scaleY * factorY,
    };
  });

  return {
    label: 'Resize Image',
    width: Math.round(target.width),
    height: Math.round(target.height),
    layers,
  };
}

/**
 * Change the canvas frame without touching pixels — content keeps its absolute
 * position relative to the chosen anchor.
 */
export function planCanvasResize(
  doc: EditorDocument,
  target: Size2,
  anchor: ResizeAnchor = 'center',
): TransformPlan | null {
  if (target.width < 1 || target.height < 1) return null;
  const factor = ANCHOR_FACTORS[anchor];
  const dx = Math.round((target.width - doc.width) * factor.x);
  const dy = Math.round((target.height - doc.height) * factor.y);

  const layers = leafLayers(doc.layers).map(
    (layer): LayerPlan => ({
      id: layer.id,
      x: layer.x + dx,
      y: layer.y + dy,
      op: null,
    }),
  );

  return {
    label: 'Resize Canvas',
    width: Math.round(target.width),
    height: Math.round(target.height),
    layers,
  };
}

/** Aspect ratio of a document, for lock-ratio UI. */
export function aspectRatio(doc: Size2): number {
  return doc.width / doc.height;
}
